import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  CreditCard, 
  Key,
  Save,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Eye,
  EyeOff,
  RefreshCw,
  Euro,
  Percent
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const StripeSettingsPage = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null); // null, 'connected', 'error'
  
  const [settings, setSettings] = useState({
    enabled: true,
    test_mode: true,
    publishable_key: '',
    secret_key: '',
    webhook_secret: '',
    currency: 'eur',
    payment_methods: ['card'],
    auto_capture: true,
    send_receipt: true,
    statement_descriptor: 'MSC LAGENCE',
    fees: {
      pass_to_client: false,
      percentage: 2.9,
      fixed: 0.25
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings/stripe');
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
        if (res.data.publishable_key && res.data.secret_key) {
          setConnectionStatus('connected');
        }
      }
    } catch (error) {
      console.error('Error fetching Stripe settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/stripe', settings);
      toast.success('Configuration Stripe enregistrée !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await api.post('/settings/stripe/test');
      if (res.data?.success) {
        setConnectionStatus('connected');
        toast.success('Connexion Stripe réussie !');
      } else {
        setConnectionStatus('error');
        toast.error(res.data?.message || 'Erreur de connexion');
      }
    } catch (error) {
      setConnectionStatus('error');
      toast.error('Impossible de tester la connexion');
    } finally {
      setTesting(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="stripe-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Configuration Stripe
            </h1>
            <p className="text-slate-500 mt-1">
              Gérez vos paiements par carte bancaire
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={testConnection}
              disabled={testing || !settings.publishable_key || !settings.secret_key}
            >
              {testing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Tester la connexion
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer
            </Button>
          </div>
        </div>

        {/* Connection Status */}
        {connectionStatus && (
          <Alert className={cn(
            "border-l-4",
            connectionStatus === 'connected' 
              ? "border-l-emerald-500 bg-emerald-50" 
              : "border-l-red-500 bg-red-50"
          )}>
            <AlertDescription className="flex items-center gap-2">
              {connectionStatus === 'connected' ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <span className="text-emerald-800 font-medium">
                    Stripe connecté en mode {settings.test_mode ? 'TEST' : 'PRODUCTION'}
                  </span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <span className="text-red-800 font-medium">
                    Erreur de connexion Stripe
                  </span>
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* API Keys Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                Clés API
              </CardTitle>
              <CardDescription>
                Vos clés API Stripe (Dashboard → Developers → API keys)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Test Mode Toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div>
                  <Label>Mode Test</Label>
                  <p className="text-xs text-slate-500">Utilisez les clés de test Stripe</p>
                </div>
                <div className="flex items-center gap-2">
                  {settings.test_mode && (
                    <Badge className="bg-amber-100 text-amber-700">TEST</Badge>
                  )}
                  <Switch
                    checked={settings.test_mode}
                    onCheckedChange={(checked) => updateField('test_mode', checked)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Clé publique (Publishable key)</Label>
                <Input
                  value={settings.publishable_key}
                  onChange={(e) => updateField('publishable_key', e.target.value)}
                  placeholder={settings.test_mode ? "pk_test_..." : "pk_live_..."}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500">
                  Commence par {settings.test_mode ? "pk_test_" : "pk_live_"}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Clé secrète (Secret key)</Label>
                <div className="relative">
                  <Input
                    type={showSecretKey ? "text" : "password"}
                    value={settings.secret_key}
                    onChange={(e) => updateField('secret_key', e.target.value)}
                    placeholder={settings.test_mode ? "sk_test_..." : "sk_live_..."}
                    className="font-mono text-sm pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowSecretKey(!showSecretKey)}
                  >
                    {showSecretKey ? (
                      <EyeOff className="w-4 h-4 text-slate-400" />
                    ) : (
                      <Eye className="w-4 h-4 text-slate-400" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Commence par {settings.test_mode ? "sk_test_" : "sk_live_"}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Webhook Secret (optionnel)</Label>
                <Input
                  type="password"
                  value={settings.webhook_secret}
                  onChange={(e) => updateField('webhook_secret', e.target.value)}
                  placeholder="whsec_..."
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500">
                  Pour les notifications de paiement automatiques
                </p>
              </div>

              <a 
                href="https://dashboard.stripe.com/apikeys" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline mt-2"
              >
                <ExternalLink className="w-3 h-3" />
                Obtenir mes clés sur Stripe
              </a>
            </CardContent>
          </Card>

          {/* Settings Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                Paramètres de paiement
              </CardTitle>
              <CardDescription>
                Configuration des paiements par carte
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div>
                  <Label>Paiements Stripe activés</Label>
                  <p className="text-xs text-slate-500">Accepter les paiements par CB</p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => updateField('enabled', checked)}
                />
              </div>

              <div className="space-y-2">
                <Label>Devise</Label>
                <Input
                  value={settings.currency?.toUpperCase()}
                  onChange={(e) => updateField('currency', e.target.value.toLowerCase())}
                  placeholder="EUR"
                  className="uppercase"
                  maxLength={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Descripteur de relevé bancaire</Label>
                <Input
                  value={settings.statement_descriptor}
                  onChange={(e) => updateField('statement_descriptor', e.target.value)}
                  placeholder="NOM AGENCE"
                  maxLength={22}
                />
                <p className="text-xs text-slate-500">
                  Apparaît sur le relevé bancaire du client (max 22 caractères)
                </p>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div>
                  <Label>Capture automatique</Label>
                  <p className="text-xs text-slate-500">Encaisser immédiatement</p>
                </div>
                <Switch
                  checked={settings.auto_capture}
                  onCheckedChange={(checked) => updateField('auto_capture', checked)}
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div>
                  <Label>Envoyer un reçu</Label>
                  <p className="text-xs text-slate-500">Email automatique au client</p>
                </div>
                <Switch
                  checked={settings.send_receipt}
                  onCheckedChange={(checked) => updateField('send_receipt', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Fees Card */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                Frais de transaction
              </CardTitle>
              <CardDescription>
                Gérez les frais Stripe (environ 1.4% + 0.25€ pour l'UE)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
                  <div>
                    <Label>Répercuter sur le client</Label>
                    <p className="text-xs text-slate-500 mt-1">
                      Ajouter les frais au montant
                    </p>
                  </div>
                  <Switch
                    checked={settings.fees?.pass_to_client}
                    onCheckedChange={(checked) => setSettings(prev => ({
                      ...prev,
                      fees: { ...prev.fees, pass_to_client: checked }
                    }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Pourcentage (%)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      value={settings.fees?.percentage || 2.9}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        fees: { ...prev.fees, percentage: parseFloat(e.target.value) || 0 }
                      }))}
                      className="w-24"
                    />
                    <span className="text-slate-500">%</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Frais fixes (€)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={settings.fees?.fixed || 0.25}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        fees: { ...prev.fees, fixed: parseFloat(e.target.value) || 0 }
                      }))}
                      className="w-24"
                    />
                    <span className="text-slate-500">€</span>
                  </div>
                </div>
              </div>

              {/* Fee Calculator */}
              <div className="mt-6 p-4 rounded-xl bg-indigo-50 border border-indigo-100">
                <h4 className="font-medium text-indigo-800 mb-2 flex items-center gap-2">
                  <Euro className="w-4 h-4" />
                  Exemple de calcul
                </h4>
                <p className="text-sm text-indigo-700">
                  Pour un paiement de <strong>100€</strong> :
                </p>
                <ul className="text-sm text-indigo-600 mt-2 space-y-1">
                  <li>
                    Frais Stripe : {((100 * (settings.fees?.percentage || 2.9) / 100) + (settings.fees?.fixed || 0.25)).toFixed(2)}€
                  </li>
                  <li>
                    {settings.fees?.pass_to_client ? (
                      <>Client paie : <strong>{(100 + (100 * (settings.fees?.percentage || 2.9) / 100) + (settings.fees?.fixed || 0.25)).toFixed(2)}€</strong></>
                    ) : (
                      <>Vous recevez : <strong>{(100 - (100 * (settings.fees?.percentage || 2.9) / 100) - (settings.fees?.fixed || 0.25)).toFixed(2)}€</strong></>
                    )}
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default StripeSettingsPage;
